import { useState } from "react"
import { Input, Segmented, Select } from "antd"
import type { Environment } from "@csbi/contracts"
import { DOMAIN_LABELS, type MetricDomain } from "@csbi/metric-catalog"
import {
  PRIORITIES,
  STATE_FILTERS,
  STATE_FILTER_LABELS,
  domainsInMatrix,
  environmentsInMatrix,
  matrixRows,
  projectColumns,
  stateCounts,
  type Priority,
  type StateFilter,
} from "../../../features/coverage/matrix.js"
import { useCoverage } from "../../../features/coverage/use-coverage.js"
import { PageHeader, SectionTitle } from "../../../shared/components/page-header.js"
import { CoverageTable } from "./coverage-table.js"

/**
 * ⚠ There used to be a third level, "progress by domain", removed on 2026-09-10 — the sidebar already carries
 * the same `linked/total` badge per domain, from the same `domainCoverage()`. The same numbers twice on one
 * screen costs the reader an extra step to confirm "are these the same thing".
 */

export function CoveragePage() {
  const { data, isLoading } = useCoverage()
  const summary = data?.summary

  const [search, setSearch] = useState("")
  const [domains, setDomains] = useState<MetricDomain[]>([])
  const [priorities, setPriorities] = useState<Priority[]>([])
  const [environments, setEnvironments] = useState<Environment[]>([])
  const [state, setState] = useState<StateFilter>("all")

  // The environment filter projects whole columns away first and every other filter runs after it — so the
  // `3/6` at the end of a row, the column totals in the footer and the counts on the segmented control all point at the same set of columns, and no denominator can exceed the columns on screen
  const shown = projectColumns(data, environments)

  // The counts are computed **before row filtering**: the number on the segmented control has to equal the
  // number of rows you see after clicking it, or it changes on every click and cannot be used to judge what is left
  const counts = stateCounts(shown)
  const rows = matrixRows(shown, { search, domains, priorities, state })

  return (
    <>
      <PageHeader title="Coverage Matrix" />

      {summary && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center", fontSize: 13 }}>
          <span>
            Linked{" "}
            <strong style={{ fontSize: 20, fontVariantNumeric: "proportional-nums" }}>
              {summary.metricsLinked}/{summary.metricsTotal}
            </strong>
          </span>
          <span style={{ color: "var(--csbi-ink-secondary)" }}>with data {summary.metricsWithData}</span>
          <span style={{ color: "var(--csbi-ink-secondary)" }}>
            unsupported everywhere {summary.metricsUnsupportedEverywhere}
          </span>
        </div>
      )}

      <SectionTitle>Coverage by metric</SectionTitle>

      {/* Filters always on one row, above the charts */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <Segmented<StateFilter>
          size="small"
          value={state}
          onChange={setState}
          options={STATE_FILTERS.map((f) => ({
            value: f,
            label: `${STATE_FILTER_LABELS[f]} (${counts[f]})`,
          }))}
        />
        <Select<MetricDomain[]>
          mode="multiple"
          allowClear
          size="small"
          value={domains}
          onChange={setDomains}
          placeholder="All domains"
          style={{ minWidth: 200 }}
          maxTagCount="responsive"
          options={domainsInMatrix(data).map((d) => ({ value: d, label: DOMAIN_LABELS[d] }))}
        />
        <Select<Priority[]>
          mode="multiple"
          allowClear
          size="small"
          value={priorities}
          onChange={setPriorities}
          placeholder="All priorities"
          style={{ minWidth: 150 }}
          maxTagCount="responsive"
          options={PRIORITIES.map((p) => ({ value: p, label: p }))}
        />
        {/* The environment filter filters **columns**, not rows. With a single deployment it filters nothing, so it is simply hidden */}
        {environmentsInMatrix(data).length > 1 && (
          <Select<Environment[]>
            mode="multiple"
            allowClear
            size="small"
            value={environments}
            onChange={setEnvironments}
            placeholder="All environments"
            style={{ minWidth: 170 }}
            maxTagCount="responsive"
            options={environmentsInMatrix(data).map((e) => ({ value: e, label: e }))}
          />
        )}
        <Input
          size="small"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Metric name or id"
          style={{ width: 220 }}
        />
      </div>

      <CoverageTable rows={rows} tenants={shown?.tenants ?? []} loading={isLoading} />

    </>
  )
}
